import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL; // Use environment variable for API base URL

const CurrencyContext = createContext();

const fallbackRates = {
  NGN: 1,
  USD: 0.00065,
  EUR: 0.00060,
  GBP: 0.00052,
};

const currencySymbols = {
  NGN: '₦',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('preferredCurrency') || 'NGN';
  });
  const [rates, setRates] = useState(fallbackRates);

  // Fetch rates from backend
  useEffect(() => {
    axios
      .get(`${API}/api/exchange-rates`)
      .then((res) => {
        console.log('API response:', res.data); // debug
        const data = res.data;
        // Extract the actual rates object
        let fetchedRates = data;
        if (data && data.rates) {
          fetchedRates = data.rates; // take the inner rates object
        }
        // Ensure it's a proper object with keys
        if (fetchedRates && typeof fetchedRates === 'object' && Object.keys(fetchedRates).length > 0) {
          // Merge with fallback to fill any missing currencies (optional)
          setRates({ ...fallbackRates, ...fetchedRates });
        } else {
          console.warn('Fetched rates empty or invalid, keeping fallback');
        }
      })
      .catch(() => {
        console.warn('Using fallback exchange rates');
      });
  }, []);

  const changeCurrency = useCallback((code) => {
    setCurrency(code);
    localStorage.setItem('preferredCurrency', code);
  }, []);

  const convert = useCallback(
    (amountInNGN) => {
      const rate = rates[currency] || 1;
      console.log(`Convert ${amountInNGN} NGN → ${currency} rate:`, rate);
      return amountInNGN * rate;
    },
    [currency, rates]
  );

  const formatCurrency = useCallback(
    (amountInNGN) => {
      const converted = convert(amountInNGN);
      const symbol = currencySymbols[currency] || '₦';
      const decimals = currency === 'NGN' ? 0 : 2;
      return `${symbol}${converted.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}`;
    },
    [convert, currency]
  );

  return (
    <CurrencyContext.Provider value={{ currency, changeCurrency, convert, formatCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}